/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import {
  Colors,
  LegacyButton,
  Link,
  Spacer,
  Text,
} from '@zapier/design-system';

import { ZAP_EDITOR_URL } from 'app/common/constants';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';

import { ZapPrefillSelect } from '../ZapPrefillSelect';

const zapPrefillGeneratorStyles = css`
  padding: 22px 45px;
`;

const zapPrefillGeneratorTextWrapperStyles = css`
  margin-top: 25px;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const zapPrefillGeneratorPartnerExampleStyles = css`
  display: flex;
  align-items: center;
  margin-top: 20px;
  gap: 12px;

  div {
    display: inline-flex;
    align-items: center;

    img {
      box-sizing: content-box;
      margin: 0 6px;
      padding: 5px;
      border: 1px solid ${Colors.GrayWarm3};
      border-radius: 4px;

      & + a {
        font-size: 16px;
      }
    }
  }
`;

const zapPrefillGeneratorExampleStyles = css`
  margin: 20px 0 !important; // Override AppBuilder styles
  padding: 25px;
  background: ${Colors.PrimeBlack};
  color: ${Colors.PrimeWhite};
  white-space: pre-wrap;
  border-radius: 5px;

  span {
    color: ${Colors.Yellow2};
  }
`;

const linkStyles = css`
  text-decoration: none;
  font-weight: 600;
`;

type ZapPrefillGeneratorProps = {
  serviceSlug: string;
};

function ZapPrefillGenerator({
  serviceSlug,
}: ZapPrefillGeneratorProps): JSX.Element {
  const zapURLWithParams = new URL(
    `/v1/embed/${serviceSlug}/create`,
    'https://api.zapier.com'
  );
  const zapBaseURL = new URL(zapURLWithParams.href);
  zapURLWithParams.search =
    'steps[0][app]=WeatherCLIAPI@latest&steps[0][action]=today_forecast&steps[0][params][latitude]=40.7127&steps[0][params][longitude]=-74.0059&steps[1][app]=SlackAPI&steps[1][action]=direct_message';

  return (
    <main css={zapPrefillGeneratorStyles}>
      <Text tag="h1" type="PageHeader">
        Pre-filled Zaps
      </Text>
      <div css={zapPrefillGeneratorTextWrapperStyles}>
        <div>
          <Text tag="p" type="Body1">
            Prefilling fields for your users simplifies their Zap creation
            experience, which in turn allows them to get more value out of our
            product!
          </Text>
          <div css={zapPrefillGeneratorPartnerExampleStyles}>
            <Text fontWeight={600}>
              Integration that pre-fills Zaps for their users:{' '}
            </Text>
            <div>
              <img
                alt=""
                height={20}
                src="https://zapier-images.imgix.net/storage/services/9d8fbcdab89ea723d94772ff0170a99a.png"
                width={20}
              />{' '}
              <Link
                css={linkStyles}
                href="https://cdn.zappy.app/0625a071fbee914968480b0b58d696a8.mp4"
                target="_blank"
              >
                Paperform
              </Link>
            </div>
          </div>
        </div>
        <div>
          <Text margin="0 0 15px 0" tag="h2" type="ParagraphHeader3">
            Try an example pre-filled Zap
          </Text>
          <Text tag="p" type="Body1">
            This URL will take you to the Zap Editor to create a Zap that will{' '}
            <Text type="Body3">
              send you a Slack message with today’s weather forecast based on a
              location.
            </Text>{' '}
            Latitude and longitude will be pre-filled on behalf of the user.
            Test it out to see how it works!
          </Text>
          <pre css={zapPrefillGeneratorExampleStyles}>
            {zapBaseURL.href}
            ?steps[0][app]=WeatherCLIAPI@latest&steps[0][action]=today_forecast&steps[0][params][latitude]=
            <span>40.7127</span>&steps[0][params][longitude]=
            <span>-74.0059</span>
            &steps[1][app]=SlackAPI&steps[1][action]=direct_message
          </pre>
          <LegacyButton
            color="secondary"
            href={zapURLWithParams.href}
            target="_blank"
          >
            Test example URL
          </LegacyButton>
        </div>
        <div>
          <Text margin="0 0 15px 0" tag="h2" type="ParagraphHeader3">
            Creating pre-filled Zaps from Zap templates
          </Text>
          <Text tag="p" type="Body1">
            Effortlessly guide your users into the Zap Editor with pre-defined
            fields by leveraging the Zap Template endpoint (
            <Link
              href={
                new URL(
                  '/embed/partner-api#get-v1zap-templates',
                  BASE_DOC_URL_VB
                ).href
              }
              target="_blank"
            >
              /v1/zap-templates
            </Link>
            ) and prefills, streamlining their integration experience and
            boosting productivity.
          </Text>
          <Spacer height={20} />
          <LegacyButton
            href={new URL('#prefill-options', ZAP_EDITOR_URL).href}
            target="_blank"
          >
            How to pre-fill a Zap template
          </LegacyButton>
        </div>
        <div>
          <Text margin="0 0 15px 0" tag="h2" type="ParagraphHeader3">
            Creating a pre-filled Zap from scratch
          </Text>
          <Text tag="p" type="Body1">
            Follow these steps to create a{' '}
            <Link
              href={
                new URL(
                  '/embed/zap-editor#creating-zaps-without-zap-templates',
                  BASE_DOC_URL_VB
                ).href
              }
              target="_blank"
            >
              pre-filled Zap URL
            </Link>{' '}
            for your users. Place it anywhere inside your product to help your
            users create and publish Zaps faster.
          </Text>
          <ZapPrefillSelect serviceSlug={serviceSlug} />
        </div>
      </div>
    </main>
  );
}

export default ZapPrefillGenerator;
